import React from 'react';
import { Trophy, Users, Award, Sparkles } from 'lucide-react';

const features = [
  {
    icon: Trophy,
    title: 'Every Kid Gets a Moment',
    description: 'The recap calls out players by name — not just the stars. Every kid on that roster gets recognized.',
  },
  {
    icon: Users,
    title: 'The Team Chat Goes Crazy',
    description: 'Drop the link in the group chat and watch the reactions pour in. Parents love it. Kids love it even more.',
  },
  {
    icon: Award,
    title: 'Memories They Keep',
    description: 'This isn\'t a stat sheet. It\'s a story. One they\'ll listen to on repeat for days.',
  },
  {
    icon: Sparkles,
    title: 'Grandma Can Listen Too',
    description: 'Send the link to anyone — no app, no account needed. Just tap and play.',
  },
];

const ForTeamsSection = () => {
  return (
    <section id="made-for-teams" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* Left: text + feature chips */}
          <div>
            <span className="inline-block text-xs font-bold text-royal-blue uppercase tracking-widest mb-3">Built for Families</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight mb-5">
              Your Kid Just Played Their Heart Out. <span className="text-yellow-500">Let's Make It a Memory.</span>
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-4">
              Games go fast. The drive home is busy. Before you know it, the moment is gone. Postgame Buzz turns the final score into something your family can actually hold onto — a fun, energetic recap that makes your kid feel seen and celebrated.
            </p>
            <p className="text-gray-600 text-base leading-relaxed mb-8">
              Share it with the grandparents who couldn't make it. Put it in the family group chat. Play it on the way to school Monday morning. It's more than a recap — it's a moment worth keeping.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div key={index} className="group flex items-start gap-3 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 p-4">
                  <div className="w-9 h-9 rounded-lg bg-royal-blue/10 flex items-center justify-center shrink-0 group-hover:bg-royal-blue/20 transition-colors">
                    <feature.icon className="w-4 h-4 text-royal-blue" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{feature.title}</p>
                    <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: photo */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl hover:scale-[1.01] transition-transform duration-500">
              <img
                className="w-full h-auto object-cover"
                src="/images/yay.png"
                alt="A youth baseball team celebrating"
                loading="lazy"
              />
              {/* Dark bottom gradient for readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/30 to-transparent pointer-events-none" />
            </div>
            {/* Yellow accent corner */}
            <div className="absolute -bottom-3 -right-3 w-20 h-20 bg-yellow-400 rounded-2xl -z-10 opacity-60" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ForTeamsSection;
